import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CardMedia,
  Chip
} from '@mui/material';
import { Add, Edit, Delete, DragIndicator } from '@mui/icons-material';
import CurrencyPackForm from './CurrencyPackForm';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API_BASE_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:4000' 
  : '';

// Sortable Currency Pack Component
const SortableCurrencyPack = ({ currencyPack, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: currencyPack._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 8 : 2}
      sx={{
        transform: isDragging ? 'rotate(5deg)' : 'none',
        transition: 'all 0.2s ease',
      }}
    >
      {currencyPack.image && (
        <CardMedia
          component="img"
          height="140"
          image={currencyPack.image}
          alt={currencyPack.name}
          sx={{ objectFit: 'cover' }}
        />
      )}
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <IconButton
              {...attributes}
              {...listeners}
              size="small"
              sx={{ mr: 1, cursor: 'grab' }}
            >
              <DragIndicator />
            </IconButton>
            <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
              {currencyPack.name}
            </Typography>
          </Box>
          <Box>
            <IconButton
              size="small"
              onClick={() => onEdit(currencyPack)}
              sx={{ mr: 1 }}
            >
              <Edit />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => onDelete(currencyPack)}
            >
              <Delete />
            </IconButton>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`$${currencyPack.price}`} 
            color="primary" 
            size="small" 
          />
          <Chip 
            label={`${currencyPack.tokens} tokens`} 
            color="secondary" 
            size="small" 
          />
          <Chip 
            label={currencyPack.isActive ? 'Active' : 'Inactive'} 
            color={currencyPack.isActive ? 'success' : 'default'} 
            size="small" 
          />
        </Box>
      </CardContent>
    </Card>
  );
};

const CurrencyPackList = () => {
  const [currencyPacks, setCurrencyPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingCurrencyPack, setEditingCurrencyPack] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [currencyPackToDelete, setCurrencyPackToDelete] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    fetchCurrencyPacks();
  }, []);

  const fetchCurrencyPacks = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/currency-packs`);
      if (!response.ok) {
        throw new Error('Failed to fetch currency packs');
      }
      const data = await response.json();
      setCurrencyPacks(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCurrencyPack = async (currencyPackData) => {
    try {
      const formData = new FormData();
      formData.append('name', currencyPackData.name);
      formData.append('price', currencyPackData.price);
      formData.append('tokens', currencyPackData.tokens);
      formData.append('isActive', currencyPackData.isActive);
      
      if (currencyPackData.imageFile) {
        formData.append('image', currencyPackData.imageFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/currency-packs`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to create currency pack');
      }

      await fetchCurrencyPacks();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleUpdateCurrencyPack = async (currencyPackData) => {
    try {
      const formData = new FormData();
      formData.append('name', currencyPackData.name);
      formData.append('price', currencyPackData.price);
      formData.append('tokens', currencyPackData.tokens);
      formData.append('isActive', currencyPackData.isActive);
      
      if (currencyPackData.imageFile) {
        formData.append('image', currencyPackData.imageFile);
      } else if (currencyPackData.existingImage) {
        formData.append('existingImage', currencyPackData.existingImage);
      }

      const response = await fetch(`${API_BASE_URL}/api/currency-packs/${editingCurrencyPack._id}`, {
        method: 'PUT',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to update currency pack');
      }

      await fetchCurrencyPacks();
      setEditingCurrencyPack(null);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteCurrencyPack = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/currency-packs/${currencyPackToDelete._id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete currency pack');
      }

      await fetchCurrencyPacks();
      setCurrencyPackToDelete(null);
      setDeleteDialogOpen(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = currencyPacks.findIndex(item => item._id === active.id);
      const newIndex = currencyPacks.findIndex(item => item._id === over.id);
      
      const newItems = arrayMove(currencyPacks, oldIndex, newIndex);
      setCurrencyPacks(newItems);

      // Update order on server
      try {
        const response = await fetch(`${API_BASE_URL}/api/currency-packs/reorder`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ packs: newItems }),
        });

        if (!response.ok) {
          throw new Error('Failed to update order');
        }
      } catch (err) {
        setError(err.message);
        // Revert on error
        await fetchCurrencyPacks();
      }
    }
  };

  const openEditForm = (currencyPack) => {
    setEditingCurrencyPack(currencyPack);
    setFormOpen(true);
  };

  const openDeleteDialog = (currencyPack) => {
    setCurrencyPackToDelete(currencyPack);
    setDeleteDialogOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingCurrencyPack(null);
  };

  if (loading) {
    return <Typography>Loading currency packs...</Typography>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
        <Button variant="contained" startIcon={<Add />} onClick={() => setFormOpen(true)}>
          Add Currency Pack
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={currencyPacks.map(pack => pack._id)} strategy={verticalListSortingStrategy}>
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {currencyPacks.map((currencyPack) => (
              <SortableCurrencyPack
                key={currencyPack._id}
                currencyPack={currencyPack}
                onEdit={openEditForm}
                onDelete={openDeleteDialog}
              />
            ))}
          </Box>
        </SortableContext>
      </DndContext>

      <CurrencyPackForm
        open={formOpen}
        onClose={closeForm}
        onSubmit={editingCurrencyPack ? handleUpdateCurrencyPack : handleCreateCurrencyPack}
        currencyPack={editingCurrencyPack}
        isEdit={!!editingCurrencyPack}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Currency Pack</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{currencyPackToDelete?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteCurrencyPack} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CurrencyPackList;
